import React, { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { formatSAR } from "@/i18n";
import { Loader2, Users, UserPlus, Wallet, Clock, AlertTriangle, ShieldCheck } from "lucide-react";

export default function AdminStats({ onCustomers }) {
  const { t, lang } = useApp();
  const [s, setS] = useState(null);
  const [err, setErr] = useState(false);
  const load = () => { setErr(false); setS(null); api.get("/admin/stats").then((r) => setS(r.data)).catch(() => setErr(true)); };
  useEffect(load, []);
  const n = (v) => Number(v || 0).toLocaleString(lang === "ar" ? "ar-SA" : "en-US");

  if (err) return (
    <div className="py-12 text-center"><AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-3" />
      <p className="font-semibold">{t.loadError}</p>
      <button onClick={load} className="mt-3 text-sm font-bold text-primary">{t.retry}</button></div>
  );
  if (!s) return <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const max = Math.max(1, ...s.daily_30d.map((d) => d.count));
  const tiles = [
    { icon: Users, label: t.usersTotal, value: n(s.users.total), sub: `${n(s.users.verified)} ${t.verifiedBadge}` },
    { icon: UserPlus, label: t.usersNew7d, value: n(s.users.new_7d) },
    { icon: Wallet, label: t.liability, value: `${formatSAR(s.wallet_liability_sar, lang)}`, sub: t.currency },
    { icon: Clock, label: t.pendingPayments, value: n(s.pending_payments) },
  ];
  return (
    <div data-testid="admin-stats" className="pt-4 space-y-5">
      <div className="flex items-end justify-between">
        <h2 className="text-xl font-black flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-primary" />{t.adminOverview}</h2>
        <span className="text-[11px] text-muted-foreground">{t.lastUpdated} {new Date(s.generated_at).toLocaleTimeString(lang === "ar" ? "ar-SA" : "en-US", { hour: "2-digit", minute: "2-digit" })}</span>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {tiles.map((x) => (
          <div key={x.label} className="rounded-2xl border border-border bg-card p-4">
            <x.icon className="w-4 h-4 text-primary mb-2" />
            <p className="text-[11px] text-muted-foreground font-semibold">{x.label}</p>
            <p className="text-lg font-black tabular-nums leading-tight mt-0.5">{x.value}</p>
            {x.sub && <p className="text-[11px] text-muted-foreground">{x.sub}</p>}
          </div>
        ))}
      </div>
      <div className="rounded-2xl border border-border bg-card p-4">
        <p className="text-sm font-bold mb-3">{t.volume30d}</p>
        <div className="flex items-end gap-[3px] h-20" dir="ltr">
          {s.daily_30d.length === 0 && <p className="text-xs text-muted-foreground">{t.noActivity}</p>}
          {s.daily_30d.map((d) => (
            <div key={d.day} title={`${d.day}: ${d.count}`} className="flex-1 rounded-t bg-primary/70" style={{ height: `${Math.max(6, (d.count / max) * 100)}%` }} />
          ))}
        </div>
        <div className="mt-3 space-y-1.5">
          {s.outflow_30d_by_type.map((r) => (
            <div key={r.type} className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t[`type_${r.type}`] || r.type} · {n(r.count)}</span>
              <span className="font-bold tabular-nums">{formatSAR(r.amount, lang)} {t.currency}</span>
            </div>
          ))}
          {s.topups_30d.map((r) => (
            <div key={String(r.test_mode)} className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t.topups30d}{r.test_mode ? ` (${t.testMode})` : ""} · {n(r.count)}</span>
              <span className="font-bold tabular-nums">{formatSAR(r.amount, lang)} {t.currency}</span>
            </div>
          ))}
        </div>
      </div>
      <button onClick={onCustomers} data-testid="admin-open-customers"
        className="w-full h-12 rounded-2xl bg-primary text-primary-foreground font-bold">{t.viewCustomers}</button>
      <div className="rounded-2xl border border-border bg-card p-4">
        <p className="text-sm font-bold mb-2">{t.auditLog}</p>
        <div className="divide-y divide-border/60">
          {s.recent_audit.length === 0 && <p className="text-xs text-muted-foreground py-2">{t.noActivity}</p>}
          {s.recent_audit.map((a, i) => (
            <div key={i} className="py-2 flex justify-between gap-3 text-xs">
              <span className="font-mono font-semibold truncate">{a.action}</span>
              <span className="text-muted-foreground shrink-0" dir="ltr">{new Date(a.at).toLocaleString(lang === "ar" ? "ar-SA" : "en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
